import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { ProductMedia } from '@/components/store/product-media'

interface ProductGalleryProps {
    images: Array<{ src: string, alt: string }>
    fallbackAlt: string
    categorySlug: string | null
}

export const ProductGallery: FC<ProductGalleryProps> = ({ images, fallbackAlt, categorySlug }): ReactNode => {
    const { tx } = useI18n()
    const [active, setActive] = useState(0)
    const current = images[active] ?? null

    return (
        <div className='flex flex-col gap-3'>
            <div className='relative overflow-hidden rounded-[2rem] bg-white ring-1 ring-navy-100'>
                <ProductMedia
                    src={current?.src ?? null}
                    alt={current?.alt ?? fallbackAlt}
                    categorySlug={categorySlug}
                    priority
                    className='aspect-square w-full'
                    iconClassName='size-[42%]'
                />
            </div>

            {images.length > 1 && (
                <div className='flex gap-2 overflow-x-auto pb-1 no-scrollbar' role='tablist' aria-label={tx('صور المنتج', 'Product photos')}>
                    {images.map((image, index) => (
                        <button
                            key={image.src}
                            type='button'
                            role='tab'
                            aria-selected={index === active}
                            aria-label={tx(`عرض الصورة ${index + 1}`, `Show photo ${index + 1}`)}
                            onClick={() => setActive(index)}
                            className={cn('size-20 shrink-0 overflow-hidden rounded-2xl ring-2 transition-all sm:size-24', index === active ? 'ring-cyan-500' : 'ring-transparent opacity-70 hover:opacity-100')}
                        >
                            <img src={image.src} alt='' loading='lazy' decoding='async' className='h-full w-full object-cover' />
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
